import { InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../../utils/context/TargetingContext.tsx';
import { useCustomOptionsContext } from '../../../../utils/hooks/useCustomOptionsContext.tsx';
import { useAppInventoryAllowed } from '../../../shared/InventoryAndContent/featureFlags.ts';

type UseInventoryContentOptionsProps = {
  isBulk?: boolean;
  parentAccountFeatures: Record<string, boolean>;
};

export const useInventoryContentOptions = ({
  isBulk = false,
  parentAccountFeatures,
}: UseInventoryContentOptionsProps) => {
  const { useCurrentInstanceState } = useTargetingContext();
  const customOptions = useCustomOptionsContext<InventoryContentOption>();
  const { t } = useTranslation();
  const { isBefInstance } = useCurrentInstanceState();
  const isAppInventoryEnabled = useAppInventoryAllowed();

  let labels: Record<string, string> = {
    [InventoryContentOption.APP_BUNDLE_ID]: t('App Bundle ID'),
    [InventoryContentOption.INVENTORY]: t('Ad Unit'),
    [InventoryContentOption.PAGE_URL]: t('Page URL'),
    [InventoryContentOption.ADUNIT]: t('Ad Unit ID'),
    [InventoryContentOption.ADUNIT_SIZE]: t('Ad Unit Size'),
    [InventoryContentOption.PUBLISHER_ID]: t('Publisher ID'),
    [InventoryContentOption.INSTANCE_HASH]: t('Instance Hash'),
    [InventoryContentOption.SITE]: t('Site ID'),
    [InventoryContentOption.KEYWORDS]: t('Keywords'),
    [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: t('App Bundle ID'),
    [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: t('Custom Blocking'),
  };

  let bulkOptions: Record<string, string> = {
    [InventoryContentOption.ADUNIT]: t('Ad Unit ID'),
    [InventoryContentOption.APP_BUNDLE_ID]: t('App Bundle ID'),
    [InventoryContentOption.PAGE_URL]: t('Page URL'),
    [InventoryContentOption.PUBLISHER_ID]: t('Publisher ID'),
    [InventoryContentOption.INSTANCE_HASH]: t('Instance Hash'),
    [InventoryContentOption.SITE]: t('Site ID'),
    [InventoryContentOption.KEYWORDS]: t('Keywords'),
    [InventoryContentOption.LINE_ITEM_APP_BUNDLE_ID]: t('App Bundle ID'),
    [InventoryContentOption.PUBLISHER_INVENTORY_RULES]: t('Custom Blocking'),
  };

  if (!isBefInstance) {
    labels = omit(labels, [InventoryContentOption.PUBLISHER_ID, InventoryContentOption.INSTANCE_HASH]);
    bulkOptions = omit(bulkOptions, [InventoryContentOption.PUBLISHER_ID, InventoryContentOption.INSTANCE_HASH]);
  }

  const isSiteTargetingEnabled = parentAccountFeatures.isSiteTargetingEnabled;
  const isAdUnitTargetingEnabled = parentAccountFeatures.isAdUnitTargetingEnabled;

  if (customOptions) {
    labels = pick(labels, customOptions.availableOptions);
    bulkOptions = pick(bulkOptions, customOptions.availableOptions);
    !isSiteTargetingEnabled &&
      delete labels[InventoryContentOption.SITE] &&
      delete bulkOptions[InventoryContentOption.SITE];
    !isAdUnitTargetingEnabled &&
      delete labels[InventoryContentOption.ADUNIT] &&
      delete bulkOptions[InventoryContentOption.ADUNIT];
  }

  // When App Inventory is enabled (OA UI only):
  // - Hide APP_BUNDLE_ID from Type dropdown (App Inventory replaces it)
  // - Add APP_GROUP option at the beginning to match App Bundle ID's previous position
  // Note: Bulk Add for App Inventory is handled within AppInventoryTopBoxRenderer
  if (isAppInventoryEnabled) {
    delete labels[InventoryContentOption.APP_BUNDLE_ID];
    delete bulkOptions[InventoryContentOption.APP_BUNDLE_ID];
    labels = {
      [InventoryContentOption.APP_GROUP]: t('App Inventory'),
      ...labels,
    };
  }

  const getOptionLabel = (option: string) => labels[option];

  const options = isBulk ? Object.keys(bulkOptions) : Object.keys(labels);

  return {
    labels,
    bulkOptions,
    getOptionLabel,
    options,
  };
};
